import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { all, call, fork, put, select, spawn, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import Cache from './cache';
import {
  ACC_PERFECT_AGG,
  ACTIVITY_ACHIEVEMENTS,
  GOAL_REACHED_TIMES,
  HISTOGRAM_50P,
  HISTOGRAM_90P,
  HOURS_PLAY_TIME,
  NUMBER_SESSIONS,
  NUMBER_WORKOUTS,
  PERFORMANCE_ACHIEVEMENTS,
} from './constants';
import * as Selectors from './selectors';
import Service from './service';
import { AchievementsActions } from './slice';
import {
  AchievementsCacheError,
  EAchievementType,
  IAchievement,
  IAchievementsData,
  IAchievementsDataCache,
  IDetectAchievementsPayload,
  IFullAchievementsData,
  PartialAchievementsData,
} from './types';
import * as Utils from './utils';

// watcher saga to handle all async logic for the feature
export default function* achievementsWatcher() {
  yield all([
    takeEvery(AchievementsActions.initAchievements, initFeatureSaga),
    takeEvery(AchievementsActions.loadAchievements, loadAchievementsSaga),
    takeEvery(AchievementsActions.detectAchievements, detectAchievementsSaga),
    takeEvery(AchievementsActions.achievementsReady, startAchievementsSavingSaga),
    takeLatest(AchievementsActions.storeAchievements, saveAchievementsSaga),
  ]);
}

// initialize service with userId
export function* initFeatureSaga(action: PayloadAction<number>) {
  const userId = action.payload;
  yield call(Service.init, ApiService);
  yield call(Cache.init, userId);
}

export function* loadAchievementsSaga() {
  // check for data in cache
  const cacheValue: IAchievementsDataCache | undefined = yield call(Cache.getAchievements);
  const isExpired: boolean = yield call(Cache.isExpired, cacheValue);
  if (!cacheValue || isExpired) {
    // start loading data from api if data not present or expired
    yield spawn(fetchAchievementsFromApi);
  }
  if (cacheValue) {
    // update state from cache regardless of cache expiration
    yield put(AchievementsActions.achievementsLoaded(cacheValue.achievements));
  }
}

export function* fetchAchievementsFromApi() {
  let achievementsData: IAchievementsData;
  let success = false;
  try {
    // fetch data from api
    const achievementsResponse: AxiosResponse<IFullAchievementsData> = yield call(Service.getUserAchievements);
    achievementsData = yield call(Utils.parseAchievements, achievementsResponse.data);
    success = true;
  } catch (error) {
    achievementsData = {
      single: [],
      multi: [],
    };
  }
  const isLoading = yield select(Selectors.isLoading);
  // if data was not loaded yet or fresh data was successfully loaded
  if (isLoading || success) {
    if (success) {
      // get merged data
      achievementsData = yield call(updateCache, achievementsData);
    }
    // update state with freshly loaded achievements or merged with cache
    yield put(AchievementsActions.achievementsLoaded(achievementsData));
  }
}

export function* updateCache(newData: IAchievementsData) {
  // default case - we save just new received data
  let dataToCache = newData;
  // check existing data from cache
  const cacheValue: IAchievementsDataCache | undefined = yield call(Cache.getAchievements);
  if (cacheValue) {
    const { single, multi } = cacheValue.achievements;
    // get unsync data from cache
    const unsyncSingle = yield call(Utils.filterBySyncTimestamp, single);
    const unsyncMulti = yield call(Utils.filterBySyncTimestamp, multi);
    const unsyncAchievements: IAchievementsData = {
      single: unsyncSingle,
      multi: unsyncMulti,
    };
    // replace sync data with received and keep unsync data
    dataToCache = yield call(Utils.mergeAchievements, unsyncAchievements, newData);
  }
  // save achievements with current timestamp to cache
  try {
    yield call(Cache.setAchievements, dataToCache);
  } catch (error) {
    // ignore cache error
  }
  return dataToCache;
}

export function pushExistingAchievements(
  workoutId: number,
  singleAchievements: IAchievement[],
  singleAchievementsOld: IAchievement[],
  subType: number
) {
  const existingAchievement = Utils.findNewLevelAchievement(singleAchievementsOld, subType);
  if (!existingAchievement) {
    singleAchievements.push(Utils.createAchievement(workoutId, EAchievementType.NewLevelUnlocked, subType));
  }
}

export function pushNewLevelUnlocked(
  endDifficulty: number,
  maxDifficulty: number,
  workoutId: number,
  singleAchievements: IAchievement[],
  singleAchievementsOld: IAchievement[]
) {
  // level in unity is 0-based but we skip the first level same as app
  for (let i = 1; i <= endDifficulty; i++) {
    pushExistingAchievements(workoutId, singleAchievements, singleAchievementsOld, i);
  }
  // maxDifficulty is also 0-based same as unity
  if (endDifficulty === maxDifficulty) {
    pushExistingAchievements(workoutId, singleAchievements, singleAchievementsOld, 0);
  }
}

export function* detectAchievementsSaga(action: PayloadAction<IDetectAchievementsPayload>) {
  const { workoutId, siblings, progress, maxDifficulty, comparison, totalGameplays, goal, bestResult } = action.payload;
  const singleAchievementsOld: IAchievement[] = yield select(Selectors.getSingleAchievementsForSiblings, siblings);
  const singleAchievements: IAchievement[] = [];
  const multiAchievements: IAchievement[] = [];
  // GoalReached achievement calculation
  if (progress.stats.score >= goal) {
    multiAchievements.push(Utils.createAchievement(workoutId, EAchievementType.GoalReached, -1));
  }
  // AccuracyPerfect achievement calculation
  if (progress.stats.accuracy >= 1) {
    multiAchievements.push(Utils.createAchievement(workoutId, EAchievementType.AccuracyPerfect, -1));
  }
  // NewLevelUnlocked achievement calculation
  pushNewLevelUnlocked(
    progress.stats.endDifficulty,
    maxDifficulty,
    workoutId,
    singleAchievements,
    singleAchievementsOld
  );

  // HistogramPercentage achievement calculation
  const performance = comparison;
  for (const p of PERFORMANCE_ACHIEVEMENTS) {
    if (performance < p) {
      continue;
    }
    const existingAchievement = yield call(Utils.findHistogramPercentage, singleAchievementsOld, p);
    if (!existingAchievement) {
      singleAchievements.push(Utils.createAchievement(workoutId, EAchievementType.HistogramPercentage, p));
    }
  }
  // PlayTimes achievement calculation
  for (const a of ACTIVITY_ACHIEVEMENTS) {
    if (totalGameplays < a) {
      continue;
    }
    const existingAchievement = yield call(Utils.findPlayTimes, singleAchievementsOld, a);
    if (!existingAchievement) {
      singleAchievements.push(Utils.createAchievement(workoutId, EAchievementType.PlayTimes, a));
    }
  }
  // HighScore achievement calculation
  if (typeof bestResult !== 'undefined' && progress.stats.score >= bestResult) {
    multiAchievements.push(Utils.createAchievement(workoutId, EAchievementType.HighScore, -1));
  }
  // global achievements
  const globalAchievements: IAchievement[] = yield call(
    detectGlobalAchievementsSaga,
    singleAchievements,
    multiAchievements
  );
  // accumulate all detected achievements
  const currentAchievements: IAchievementsData = {
    single: [...singleAchievements, ...globalAchievements],
    multi: multiAchievements,
  };
  // show UI and start saving
  yield put(AchievementsActions.achievementsReady(currentAchievements));
}

export function* pushNumberWorkouts(achievements: IAchievement[], existingGlobalAchievements: IAchievement[]) {
  const numberWorkouts = yield select(Selectors.getWorkoutsNumber);
  for (const n of NUMBER_WORKOUTS) {
    if (numberWorkouts < n) {
      continue;
    }
    const existingAchievement = yield call(Utils.findWorkoutNumberAchievement, existingGlobalAchievements, n);
    if (!existingAchievement) {
      achievements.push(Utils.createAchievement(0, EAchievementType.WorkoutNumber, n));
    }
  }
}

export function* pushPlayTimeLength(achievements: IAchievement[], existingGlobalAchievements: IAchievement[]) {
  const playTimeLength = yield select(Selectors.getPlayTimeLength);
  for (const n of HOURS_PLAY_TIME) {
    if (playTimeLength < n) {
      continue;
    }
    const existingAchievement = yield call(Utils.findPlayTimeLengthAchievement, existingGlobalAchievements, n);
    if (!existingAchievement) {
      achievements.push(Utils.createAchievement(0, EAchievementType.PlayTimeLength, n));
    }
  }
}

export function* pushFinishedSessions(achievements: IAchievement[], existingGlobalAchievements: IAchievement[]) {
  const finishedSessions = yield select(Selectors.getFinishedSessions);
  for (const n of NUMBER_SESSIONS) {
    if (finishedSessions < n) {
      continue;
    }
    const existingAchievement = yield call(Utils.findFinishedSessionsAchievement, existingGlobalAchievements, n);
    if (!existingAchievement) {
      achievements.push(Utils.createAchievement(0, EAchievementType.FinishedSessions, n));
    }
  }
}

export function* pushGoalReached(
  achievements: IAchievement[],
  existingGlobalAchievements: IAchievement[],
  existingMultiAchievements: IAchievement[],
  newMultiAchievements: IAchievement[]
) {
  const goalReachedTimes = yield call(Utils.countGoalReachedAchievements, [
    ...existingMultiAchievements,
    ...newMultiAchievements,
  ]);
  for (const n of GOAL_REACHED_TIMES) {
    if (goalReachedTimes < n) {
      continue;
    }
    const existingAchievement = yield call(Utils.findGoalReachedTimesAchievement, existingGlobalAchievements, n);
    if (!existingAchievement) {
      achievements.push(Utils.createAchievement(0, EAchievementType.GoalReachedTimes, n));
    }
  }
}

export function* pushAccuracyPerfect(
  achievements: IAchievement[],
  existingGlobalAchievements: IAchievement[],
  existingMultiAchievements: IAchievement[],
  newMultiAchievements: IAchievement[]
) {
  const accuracyPerfectCount = yield call(Utils.countAccuracyPerfectAchievements, [
    ...existingMultiAchievements,
    ...newMultiAchievements,
  ]);
  for (const n of ACC_PERFECT_AGG) {
    if (accuracyPerfectCount < n) {
      continue;
    }
    const existingAchievement = yield call(Utils.findAccuracyPerfectAggAchievement, existingGlobalAchievements, n);
    if (!existingAchievement) {
      achievements.push(Utils.createAchievement(0, EAchievementType.AccuracyPerfectAgg, n));
    }
  }
}

export function* detectGlobalAchievementsSaga(
  newSingleAchievements: IAchievement[],
  newMultiAchievements: IAchievement[]
) {
  const existingMultiAchievements: IAchievement[] = yield select(Selectors.getAllMultiAchievements);
  const existingSingleAchievements: IAchievement[] = yield select(Selectors.getAllSingleAchievements);
  const existingGlobalAchievements: IAchievement[] = yield select(Selectors.getGlobalAchievements);
  const achievements: IAchievement[] = [];
  // workoutNumber achievement
  yield call(pushNumberWorkouts, achievements, existingGlobalAchievements);

  // playTimeLength achievement
  yield call(pushPlayTimeLength, achievements, existingGlobalAchievements);

  // FinishedSessions achievement
  yield call(pushFinishedSessions, achievements, existingGlobalAchievements);

  // goal reached x times
  yield call(
    pushGoalReached,
    achievements,
    existingGlobalAchievements,
    existingMultiAchievements,
    newMultiAchievements
  );

  // accuracy perfect aggregated achievement
  yield call(
    pushAccuracyPerfect,
    achievements,
    existingGlobalAchievements,
    existingMultiAchievements,
    newMultiAchievements
  );

  // HistogramPercentage90p
  const reached90pTimes = yield call(
    Utils.countHistogramPercentageAchievements,
    [...existingSingleAchievements, ...newSingleAchievements],
    90
  );
  for (const n of HISTOGRAM_90P) {
    if (reached90pTimes < n) {
      continue;
    }
    const existingAchievement = yield call(Utils.findHistogramPercentage90pAchievement, existingGlobalAchievements, n);
    if (!existingAchievement) {
      achievements.push(Utils.createAchievement(0, EAchievementType.HistogramPercentage90p, n));
    }
  }
  // HistogramPercentage50p
  const reached50pTimes = yield call(
    Utils.countHistogramPercentageAchievements,
    [...existingSingleAchievements, ...newSingleAchievements],
    50
  );
  for (const n of HISTOGRAM_50P) {
    if (reached50pTimes < n) {
      continue;
    }
    const existingAchievement = yield call(Utils.findHistogramPercentage50pAchievement, existingGlobalAchievements, n);
    if (!existingAchievement) {
      achievements.push(Utils.createAchievement(0, EAchievementType.HistogramPercentage50p, n));
    }
  }
  return achievements;
}

export function* startAchievementsSavingSaga(action: PayloadAction<IAchievementsData>) {
  const achievements = action.payload;
  // merge new and existing achievements
  const existingAchievements: IAchievementsData = yield select(Selectors.getAllAchievements);
  const updatedAchievements: IAchievementsData = yield call(
    Utils.mergeAchievements,
    existingAchievements,
    achievements
  );
  // save data to cache first
  try {
    yield call(Cache.setAchievements, updatedAchievements);
  } catch (error) {
    throw new AchievementsCacheError('Unable to save achievements to cache.');
  }
  // update state and start saving
  yield put(AchievementsActions.storeAchievements(updatedAchievements));
}

export function* markCacheAsSynchronized() {
  // get current values from cache
  const cacheValue: IAchievementsDataCache = yield call(Cache.getAchievements);
  // mark all achievements as synchronized
  const synchronizedData: IAchievementsData = yield call(Utils.markCacheDataAsSynchronized, cacheValue.achievements);
  // save values to cache preserving the cache timestamp
  // while we did not actually received anything new from backend
  yield call(Cache.setAchievements, synchronizedData, cacheValue.timestamp);
}

export function* saveAchievementsSaga() {
  const toSave: IAchievementsData = yield select(Selectors.getAchievementsToSync);
  if (!toSave.single.length && !toSave.multi.length) {
    // nothing to save
    return;
  }
  // prepare data: convert from cache to server format
  const apiData: PartialAchievementsData = yield call(Utils.prepareAchievementsForSaving, toSave);
  try {
    // save data to the backend
    yield call(Service.saveUserAchievements, apiData);
    // mark data as synchronized and save to cache
    yield call(markCacheAsSynchronized);
  } catch (error) {
    // do nothing in case of error - we will try to save next time
    return;
  }
  // reload achievements to store (will refetch if necessary)
  yield fork(loadAchievementsSaga);
}
